import { Injectable, computed, inject, signal } from '@angular/core';
import {
    DocumentItemTypeEnum,
    DocumentTypeEnum,
    FavoriteFieldController,
    IaChatController,
    IaChatItem,
    IaChatNextMessage,
    IaChatTurn,
    OrderController,
    OrderEntity,
    UserController,
    OrderStatusEnum,
} from '@domain';
import { TranslateService } from '@ngx-translate/core';
import { RaToastService } from '@shared/components';
import { EMPTY, finalize, forkJoin, from, switchMap, take } from 'rxjs';
import { CompanyController } from 'src/app/domain/controllers/company/company.controller';
import { MediaUtils } from 'src/app/shared/utils/media.utils';
import { PaginationParams } from 'src/app/domain/DTOs/paginate.entity';
import { CurrencyConfig, FALLBACK_CURRENCY, resolveCurrency } from 'src/app/shared/constants/currency.constant';
import { isIaChatFinishRequest, parseIaChatAnswer } from '../models/ia-chat-answer.parser';
import { createIaChatMessage, IaChatMessage } from '../models/ia-chat-message.model';
import {
    amountModeOf,
    IaChatQuickReply,
    IaChatQuickReplyOption,
    IaChatQuoteStage,
    nextStageFromReply,
    nextStageFromText,
    questionKeyOfStage,
    quickRepliesOfStage,
    topicOfStage,
} from '../models/ia-chat-quote-script';

const STORAGE_KEY = 'ia_chat_draft';

/**
 * POC `chat-live-ia`: quem conduz a conversa é a IA (endpoint `ia-chat/next-message`),
 * e não o roteiro local. O roteiro continua no código como FALLBACK — é ele que
 * segura a conversa quando a rede/o saldo/a IA falham.
 *
 * Desligar aqui volta 100% ao comportamento anterior.
 */
const LIVE_IA_ENABLED = true;

interface IaChatDraft {
    documentType: DocumentTypeEnum | null;
    messages: IaChatMessage[];
    /** Etapa do roteiro do orçamento (ausente nos rascunhos anteriores a ele). */
    quoteStage?: IaChatQuoteStage | null;
}

@Injectable()
export class IaChatStore {
    public messages = signal<IaChatMessage[]>([]);
    public documentType = signal<DocumentTypeEnum | null>(null);
    public draft = signal('');
    public isTyping = signal(false);
    public isGenerating = signal(false);
    public orders = signal<OrderEntity[]>([]);
    public loadingOrders = signal(false);
    public showServiceResolver = signal(false);
    public showAttachMenu = signal(false);
    public showFavoriteFields = signal(false);
    public favoriteFields = signal<{ _id?: string; id?: string; description?: string }[]>([]);
    /** Recebe o orderId quando a geração do documento é solicitada com sucesso. */
    public concluded = signal<string | null>(null);
    /** Etapa do roteiro do ORÇAMENTO (null nos outros tipos e na conversa livre). */
    public quoteStage = signal<IaChatQuoteStage | null>(null);

    public canConclude = computed(
        () => this.documentType() !== null && this.messages().some(message => message.sender === 'user')
    );

    /**
     * O composer fica bloqueado até o usuário escolher o tipo do documento
     * (o seletor é a primeira mensagem do chat).
     */
    public inputLocked = computed(() => this.documentType() === null);

    private _entryOrderId: string | null = null;
    /** `true` = destino já definido como documento AVULSO (pula o resolver). */
    private _entryStandalone = signal(false);
    private _ackedOnce = false;

    /**
     * Conversa encerrada (gerou o documento ou foi descartada): daqui em diante NADA
     * pode voltar a gravar o rascunho.
     *
     * Sem isso, qualquer trabalho assíncrono que termine depois do Concluir (a extração
     * da miniatura, a próxima fala da IA) chama `_persist()` e RESSUSCITA o rascunho com
     * todas as `mediaKeys` da conversa. No próximo `init()` o `_purgeLeftoverDraft()`
     * manda essas keys para o `deleteMedia` — e elas são as URLs que o documento recém
     * gerado está usando. Ou seja: o documento perderia as mídias.
     */
    private _finished = false;
    private _ackTimer?: ReturnType<typeof setTimeout>;
    private _liveIaAskTimer?: ReturnType<typeof setTimeout>;
    private _controller = inject(IaChatController);
    private _orderController = inject(OrderController);
    private _favoriteFieldController = inject(FavoriteFieldController);
    private _companyController = inject(CompanyController);
    private _userController = inject(UserController);
    private _toast = inject(RaToastService);
    private _translate = inject(TranslateService);
    private _mediaUtils = inject(MediaUtils);

    public init(orderId: string | null, standalone: boolean = false): void {
        this._finished = false;
        this._entryOrderId = orderId;
        this._entryStandalone.set(standalone);
        // Todo chat nasce ZERADO (decisão de produto): sair descarta a conversa,
        // então não há rascunho a restaurar. O que o storage ainda guarda de uma
        // sessão interrompida (aba fechada, crash) é só lixo com mídias órfãs —
        // limpamos antes de semear.
        this._purgeLeftoverDraft();
        this._seedInitial();
    }

    /** Apaga as mídias de um rascunho abandonado e limpa o storage. */
    private _purgeLeftoverDraft(): void {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return;
        localStorage.removeItem(STORAGE_KEY);
        try {
            const data = JSON.parse(raw) as IaChatDraft;
            const keys = (data.messages ?? []).flatMap(message => message.mediaKeys ?? []);
            if (keys.length) this._controller.deleteMedia(keys).pipe(take(1)).subscribe();
        } catch {
            // Rascunho ilegível: nada mais a limpar além do storage.
        }
    }

    /**
     * Descarta a conversa ao SAIR do chat (confirmado pelo usuário): apaga as
     * mídias já enviadas e o rascunho. Fire-and-forget — o modal fecha na hora;
     * se a rede falhar, o purge do próximo init pega o resto.
     */
    public discardConversation(): void {
        this._finished = true;
        const keys = this.messages().flatMap(message => message.mediaKeys ?? []);
        if (keys.length) this._controller.deleteMedia(keys).pipe(take(1)).subscribe();
        localStorage.removeItem(STORAGE_KEY);
    }

    /** Há algo a perder saindo? (alguma contribuição do usuário) */
    public hasUserContent(): boolean {
        return this.messages().some(message => message.sender === 'user');
    }

    /**
     * Moeda da EMPRESA — é ela que o documento herda. Resolvida a cada uso porque
     * a company pode chegar depois do chat abrir.
     */
    private get _currency(): CurrencyConfig {
        try {
            const company = this._companyController.getCurrentCompany();
            return resolveCurrency(company?.documentConfig?.configs?.currencyCode);
        } catch {
            return FALLBACK_CURRENCY;
        }
    }

    public destroy(): void {
        clearTimeout(this._ackTimer);
        clearTimeout(this._liveIaAskTimer);
    }

    public selectType(type: DocumentTypeEnum): void {
        this.documentType.set(type);

        // O orçamento entra no roteiro (itens → taxas → desconto) em vez do convite
        // genérico "mande tudo": são três áreas distintas do documento e perguntar
        // uma por vez é o que permite marcar cada resposta com o seu tópico.
        const isQuote = type === DocumentTypeEnum.QUOTE;
        const key = isQuote ? 'iaChat.quoteItemsQuestion' : 'iaChat.typeChosen';

        this.messages.update(messages => [
            ...messages.filter(message => message.type !== 'typeSelection'),
            createIaChatMessage({ sender: 'ia', type: 'text', text: this._t(key), translationKey: key }),
        ]);

        if (isQuote) this.quoteStage.set('items');
        this._persist();
    }

    // ------------------------------------------------------------ quote script

    /** Tópico com que as falas atuais são marcadas (null fora do roteiro). */
    private get _activeTopic() {
        const stage = this.quoteStage();
        return stage ? (topicOfStage(stage) ?? undefined) : undefined;
    }

    /** Opções de uma mensagem de botões, já com o rótulo resolvido para a tela. */
    public quickReplyOptionsOf(message: IaChatMessage): IaChatQuickReplyOption[] {
        // Botões da IA já vêm escritos: o rótulo é a própria resposta.
        if (message.quickReplyLabels?.length) {
            return message.quickReplyLabels.map(label => ({ value: label, label }));
        }

        return (message.quickReplies ?? []).map(value => ({ value, label: this._quickReplyLabel(value) }));
    }

    /**
     * Clique num botão: vira uma fala do usuário (para a IA ler) e avança a
     * conversa — pela IA quando foi ela que sugeriu o botão, pelo roteiro local
     * quando o botão é do script.
     */
    public answerQuickReply(message: IaChatMessage, value: string): void {
        if (message.answered) return;
        // Esta e qualquer outra pergunta de botões pendente saem de cena.
        this._dismissPendingQuickReplies();

        if (message.quickReplyLabels?.length) {
            this._append(createIaChatMessage({ sender: 'user', type: 'text', text: value }));
            this._askLiveIa();
            return;
        }

        const reply = value as IaChatQuickReply;
        const stage = this.quoteStage() ?? 'done';
        const mode = amountModeOf(reply);

        // O modo escolhido pertence ao VALOR, que veio na fala anterior — grudamos
        // nela para o backend não precisar costurar as duas mensagens.
        if (mode) this._tagLastUserMessageMode(mode);

        this._append(
            createIaChatMessage({
                sender: 'user',
                type: 'text',
                text: this._quickReplyLabel(reply),
                topic: this._activeTopic,
                amountMode: mode ?? undefined,
            })
        );

        this._goToStage(nextStageFromReply(stage, reply));
    }

    /** Rótulo do botão: Sim/Não vêm do i18n, o modo é a moeda da empresa e "%". */
    private _quickReplyLabel(reply: IaChatQuickReply): string {
        switch (reply) {
            case 'yes':
                return this._t('iaChat.quoteYes');
            case 'no':
                return this._t('iaChat.quoteNo');
            case 'fixed':
                return this._currency.symbol;
            default:
                return '%';
        }
    }

    private _tagLastUserMessageMode(mode: 'fixed' | 'percentage'): void {
        const messages = this.messages();
        for (let index = messages.length - 1; index >= 0; index--) {
            const message = messages[index];
            if (message.sender === 'user' && message.type === 'text') {
                this._updateMessage(message.id, { amountMode: mode });
                return;
            }
        }
    }

    /** Idioma em que a pergunta foi feita — é o idioma em que a resposta tende a vir. */
    private get _languageCode(): string {
        return this._translate.currentLang ?? this._translate.defaultLang ?? 'pt';
    }

    /** Analisa a resposta escrita e avança o roteiro, se ele estiver rodando. */
    private _advanceScript(text: string): void {
        const stage = this.quoteStage();
        if (!stage || stage === 'done') return;

        const currency = this._currency;
        const answer = parseIaChatAnswer(text, {
            currencySymbol: currency.symbol,
            currencyCode: currency.code,
            languageCode: this._languageCode,
        });

        this._goToStage(nextStageFromText(stage, answer));
    }

    // -------------------------------------------------------------- IA ao vivo

    /**
     * Pede à IA a próxima fala (POC `chat-live-ia`).
     *
     * Existe porque o roteiro local só enxerga o tópico da vez: uma resposta que
     * cobria taxa E desconto na mesma frase ainda levava a pergunta do desconto.
     * Aqui quem lê a conversa inteira antes de perguntar é o modelo.
     *
     * Falhou (rede, saldo, erro da IA) → cai no roteiro local. O chat é usado em
     * campo: ficar sem próxima pergunta por causa da rede não é aceitável.
     */
    private _askLiveIa(): void {
        const type = this.documentType();
        if (!type) return;

        this.isTyping.set(true);

        this._controller
            .nextMessage(type, this._buildTurns(), this._translate.currentLang ?? 'pt-BR')
            .pipe(
                take(1),
                finalize(() => this.isTyping.set(false))
            )
            .subscribe({
                next: message => this._appendLiveIaMessage(message),
                error: () => this._fallbackToScript(),
            });
    }

    /**
     * A conversa como a IA precisa ler: as falas em ordem, com a mídia reduzida a
     * um MARCADOR ("[enviou 2 fotos]") — o modelo não vê o conteúdo, mas precisa
     * saber que algo chegou para responder ("Boa, recebi!") em vez de ignorar.
     * O marcador não é exibido a ninguém, por isso não passa por i18n.
     */
    private _buildTurns(): IaChatTurn[] {
        const turns: IaChatTurn[] = [];

        for (const message of this.messages()) {
            const role = message.sender === 'user' ? ('user' as const) : ('assistant' as const);

            if ((message.type === 'text' || message.type === 'favoriteField') && message.text?.trim()) {
                turns.push({ role, text: message.text.trim() });
                continue;
            }

            const marker = this._mediaTurnMarker(message);
            if (marker) turns.push({ role, text: marker });
        }

        return turns;
    }

    private _mediaTurnMarker(message: IaChatMessage): string | null {
        switch (message.type) {
            case 'image':
                return '[enviou uma foto]';
            case 'groupImage': {
                const count = Math.max(message.mediaPaths.length, message.mediaUrls.length);
                return `[enviou ${count} fotos]`;
            }
            case 'video':
                return '[enviou um vídeo]';
            default:
                return null;
        }
    }

    /** Pede a próxima fala com debounce: N uploads em sequência = UMA resposta. */
    private _askLiveIaSoon(): void {
        if (!LIVE_IA_ENABLED) {
            this._maybeAck();
            return;
        }
        clearTimeout(this._liveIaAskTimer);
        this._liveIaAskTimer = setTimeout(() => this._askLiveIa(), 800);
    }

    /** Escreve a fala da IA e, quando houver, os botões que ela sugeriu. */
    private _appendLiveIaMessage(message: IaChatNextMessage): void {
        this._append(createIaChatMessage({ sender: 'ia', type: 'text', text: message.reply }));

        if (message.quickReplies?.length) {
            this._append(
                createIaChatMessage({
                    sender: 'system',
                    type: 'quickReply',
                    // Rótulos da IA (texto livre, já no idioma da conversa) — diferente
                    // do roteiro local, onde o botão é um enum traduzido aqui.
                    quickReplyLabels: message.quickReplies,
                })
            );
        }

        // A IA diz quando já dá para gerar: encerra o roteiro local para que ele não
        // volte a perguntar nada por baixo.
        if (message.readyToFinish) this.quoteStage.set('done');

        // Confirmou ("pode criar") = o Concluir de antes. Quem encerra o chat agora
        // é a IA: gera o documento e o modal fecha pelo signal `concluded`.
        if (message.confirmedFinish) this.conclude();
    }

    /** Rede/saldo/IA falhou: segue pelo roteiro local, sem travar a conversa. */
    private _fallbackToScript(): void {
        const stage = this.quoteStage();
        const lastUserText = [...this.messages()].reverse().find(m => m.sender === 'user')?.text ?? '';

        // Sem a IA não há `confirmedFinish` — e, sem o botão "Concluir", o usuário
        // ficaria sem NENHUMA forma de gerar o documento. A leitura local do "pode
        // gerar" é o que segura esse caso (ver `isIaChatFinishRequest`).
        if (isIaChatFinishRequest(lastUserText, this._languageCode) && this.canConclude()) {
            this.conclude();
            return;
        }

        if (stage && stage !== 'done') {
            this._advanceScript(lastUserText);
            return;
        }

        this._maybeAck();
    }

    /** Move o roteiro e emite a pergunta da nova etapa (texto + botões, quando tem). */
    private _goToStage(stage: IaChatQuoteStage): void {
        if (stage === this.quoteStage()) return;
        this.quoteStage.set(stage);

        const key = questionKeyOfStage(stage);
        // A pergunta do modo cita a moeda da empresa: guardamos o parâmetro para a
        // mensagem continuar correta se o usuário trocar o idioma da interface.
        const params =
            stage === 'taxesMode' || stage === 'discountMode' ? { currency: this._currency.symbol } : undefined;

        this._append(
            createIaChatMessage({
                sender: 'ia',
                type: 'text',
                text: this._t(key, params),
                translationKey: key,
                translationParams: params,
            })
        );

        const replies = quickRepliesOfStage(stage);
        if (replies.length) {
            this._append(
                createIaChatMessage({
                    sender: 'system',
                    type: 'quickReply',
                    quickReplies: replies,
                    topic: topicOfStage(stage) ?? undefined,
                })
            );
        }
    }

    /**
     * Responder por escrito a uma pergunta de botões descarta os botões: eles já
     * não valem para a fala seguinte.
     */
    private _dismissPendingQuickReplies(): void {
        this.messages.update(messages =>
            messages.map(message => (message.type === 'quickReply' ? { ...message, answered: true } : message))
        );
    }

    public setDraft(value: string): void {
        this.draft.set(value);
    }

    public sendDraft(): void {
        this.sendText(this.draft());
        this.draft.set('');
    }

    public sendText(text: string): void {
        const trimmed = text.trim();
        if (!trimmed) return;
        this._append(
            createIaChatMessage({ sender: 'user', type: 'text', text: trimmed, topic: this._activeTopic })
        );

        // Com a IA conduzindo, ela responde a QUALQUER fala (inclusive na conversa
        // livre) — é ela quem decide se ainda falta alguma coisa.
        if (LIVE_IA_ENABLED) {
            this._dismissPendingQuickReplies();
            this._askLiveIa();
            return;
        }

        // Dentro do roteiro quem responde é a própria pergunta seguinte — o ack
        // genérico ("Anotado!") só vale na conversa livre.
        const stage = this.quoteStage();
        if (stage && stage !== 'done') {
            this._dismissPendingQuickReplies();
            this._advanceScript(trimmed);
            return;
        }

        this._maybeAck();
    }

    /**
     * Seleção ÚNICA de anexos (fotos e vídeos misturados, vários de uma vez).
     *
     * A separação aqui é só de EXIBIÇÃO no chat: várias fotos juntas viram um
     * balão em grade, vídeo é sempre individual. No documento quem decide o
     * layout (card de imagem x grupo) é a IA da geração, que remonta
     * IMAGE/GROUP_IMAGE livremente a partir das refs.
     */
    public attachFiles(files: FileList | File[]): void {
        const list = Array.from(files);
        if (!list.length) return;

        const images = list.filter(file => file.type.startsWith('image/'));
        const videos = list.filter(file => file.type.startsWith('video/'));

        if (images.length === 1) {
            this._appendMediaMessage(images, 'image', DocumentItemTypeEnum.IMAGE);
        } else if (images.length > 1) {
            this._appendMediaMessage(images, 'groupImage', DocumentItemTypeEnum.GROUP_IMAGE);
        }

        for (const video of videos) {
            this._appendMediaMessage([video], 'video', DocumentItemTypeEnum.VIDEO);
        }
    }

    public insertFavoriteField(field: { _id?: string; id?: string; description?: string }): void {
        const id = field._id ?? field.id ?? '';
        if (!id) return;
        this._append(
            createIaChatMessage({
                sender: 'user',
                type: 'favoriteField',
                text: field.description ?? '',
                favoriteFieldId: id,
            })
        );
        this.showFavoriteFields.set(false);
        this.showAttachMenu.set(false);
        if (LIVE_IA_ENABLED) this._askLiveIa();
        else this._maybeAck();
    }

    public loadFavoriteFields(): void {
        this._favoriteFieldController.getAll().pipe(take(1)).subscribe({
            next: fields => {
                this.favoriteFields.set(fields);
                this.showFavoriteFields.set(true);
            },
            error: () => this.favoriteFields.set([]),
        });
    }

    public editText(message: IaChatMessage, text: string): void {
        const trimmed = text.trim();
        if (!trimmed) return;
        this.messages.update(messages =>
            messages.map(item => (item.id === message.id ? { ...item, text: trimmed } : item))
        );
        this._persist();
    }

    public removeMessage(message: IaChatMessage): void {
        if (message.mediaKeys.length) {
            this._controller.deleteMedia(message.mediaKeys).pipe(take(1)).subscribe();
        }
        this.messages.update(messages => messages.filter(item => item.id !== message.id));
        this._persist();
    }

    /**
     * Encerra o chat e gera o documento. Chamado pela IA (`confirmedFinish`), nunca
     * por um clique: não há mais botão "Concluir".
     *
     * Por isso o guard é SILENCIOSO — sem fala do usuário para gerar, o certo é a
     * conversa continuar, não um toast de erro sobre algo que ele não pediu.
     */
    public conclude(): void {
        if (!this.canConclude()) return;

        // Botão pendente da última pergunta não sobrevive ao fim da conversa.
        this._dismissPendingQuickReplies();

        // Destino já definido na entrada: avulso (feed) ou serviço (detalhe).
        if (this._entryStandalone()) {
            this.concludeWithOrder(null);
            return;
        }

        const orderId = this._entryOrderId;
        if (!orderId) {
            this.openServiceResolver();
            return;
        }

        this.concludeWithOrder(orderId);
    }

    /**
     * Gera o documento no serviço [orderId] — ou como documento AVULSO quando
     * `null` (o backend cria a order pessoal invisível).
     */
    public concludeWithOrder(orderId: string | null): void {
        const type = this.documentType();
        if (!type || !this.canConclude()) return;

        this.isGenerating.set(true);
        this._controller
            .generateDocument(orderId, type, this._buildItems())
            .pipe(finalize(() => this.isGenerating.set(false)), take(1))
            .subscribe({
                next: () => {
                    this._finished = true;
                    localStorage.removeItem(STORAGE_KEY);
                    this.showServiceResolver.set(false);
                    this._toast.success({ message: this._t('iaChat.generatingPending') });
                    // '' sinaliza "concluído como avulso" (consumidores tratam
                    // null como "ainda não concluiu").
                    this.concluded.set(orderId ?? '');
                },
                error: error => this._toast.error({ message: error?.message ?? this._t('iaChat.generateError') }),
            });
    }

    public openServiceResolver(): void {
        this.showServiceResolver.set(true);
        this.loadingOrders.set(true);
        const paginationParams: PaginationParams = { page: 1, limit: 20 };
        this._orderController
            .getByFilter(OrderStatusEnum.PROGRESS, paginationParams)
            .pipe(finalize(() => this.loadingOrders.set(false)), take(1))
            .subscribe({
                next: paginate => this.orders.set(paginate.data ?? []),
                error: () => this.orders.set([]),
            });
    }

    private _appendMediaMessage(
        files: File[],
        type: 'image' | 'groupImage' | 'video',
        mediaType: DocumentItemTypeEnum
    ): void {
        const paths = files.map(file => URL.createObjectURL(file));
        const message = createIaChatMessage({
            sender: 'user',
            type,
            mediaPaths: paths,
            mediaSizes: files.map(file => file.size),
            uploadStatus: 'uploading',
        });
        this._append(message);

        forkJoin(
            files.map(file =>
                this._controller.uploadMedia(file, mediaType, progress =>
                    this._updateMessage(message.id, { uploadProgress: progress })
                )
            )
        )
            .pipe(take(1))
            .subscribe({
                next: results => {
                    this._updateMessage(message.id, {
                        mediaUrls: results.map(result => result.url),
                        mediaKeys: results.map(result => result.key),
                        uploadStatus: 'done',
                        uploadProgress: 100,
                    });

                    // A miniatura vem depois do vídeo e sem segurar a conversa: ela é um
                    // extra, e quem espera por ela é só o documento gerado no fim.
                    if (type === 'video') this._attachVideoPoster(message.id, files[0]);
                    // TODA mensagem merece resposta — mídia inclusive. Debounce
                    // para uma sequência de fotos virar UMA fala da IA, não três.
                    this._askLiveIaSoon();
                },
                error: () => this._updateMessage(message.id, { uploadStatus: 'error' }),
            });
    }

    /**
     * Extrai o quadro do vídeo já anexado, sobe como mídia do chat e guarda a URL na
     * mensagem — é o que dá miniatura ao vídeo no documento gerado pelo chat, onde quem
     * cria o campo é o worker do backend (que não decodifica vídeo).
     *
     * Best-effort: vídeo do qual não sai quadro, ou upload da miniatura que falha,
     * seguem sem ela.
     */
    private _attachVideoPoster(messageId: string, file: File): void {
        from(this._mediaUtils.getVideoPosterAndDuration(file))
            .pipe(
                switchMap(({ poster, durationSeconds }) => {
                    if (durationSeconds) {
                        this._updateMessage(messageId, { mediaDurations: [durationSeconds] });
                    }

                    if (!poster) return EMPTY;
                    return this._controller.uploadMedia(poster, DocumentItemTypeEnum.IMAGE);
                }),
                take(1)
            )
            .subscribe({
                next: result => {
                    // Conversa encerrada enquanto a miniatura subia: ela não entrou no
                    // payload do documento (montado no Concluir) e o rascunho não pode mais
                    // ser regravado (`_finished`) — sem apagar aqui, o arquivo fica no bucket
                    // sem dono, invisível para todo mundo.
                    if (this._finished) {
                        this._controller
                            .deleteMedia([result.key])
                            .pipe(take(1))
                            .subscribe({ error: () => undefined });
                        return;
                    }

                    const message = this.messages().find(item => item.id === messageId);

                    this._updateMessage(messageId, {
                        mediaThumbUrls: [result.url],
                        // A miniatura é mídia do chat como qualquer outra: entra nas keys
                        // para o cleanup do "resetar conversa" apagá-la junto do vídeo.
                        mediaKeys: [...(message?.mediaKeys ?? []), result.key],
                    });
                },
                error: () => {
                    // Sem miniatura o vídeo continua valendo; o PDF usa o card sem foto.
                },
            });
    }

    private _buildItems(): IaChatItem[] {
        return this.messages()
            .filter(message => message.sender === 'user')
            .map(message => {
                switch (message.type) {
                    case 'text':
                        // topic/mode só existem no roteiro do orçamento; no resto vão
                        // ausentes e o backend classifica a fala pelo conteúdo.
                        return {
                            kind: 'text',
                            text: message.text,
                            topic: message.topic,
                            mode: message.amountMode,
                        };
                    case 'favoriteField':
                        return {
                            kind: 'favoriteField',
                            favoriteFieldId: message.favoriteFieldId,
                            text: message.text,
                            topic: message.topic,
                        };
                    case 'image':
                        return { kind: 'image', mediaUrls: message.mediaUrls, mediaSizes: message.mediaSizes ?? [] };
                    case 'groupImage':
                        return { kind: 'groupImage', mediaUrls: message.mediaUrls, mediaSizes: message.mediaSizes ?? [] };
                    case 'video':
                        return {
                            kind: 'video',
                            mediaUrls: message.mediaUrls,
                            mediaSizes: message.mediaSizes ?? [],
                            mediaThumbUrls: message.mediaThumbUrls ?? [],
                            mediaDurations: message.mediaDurations ?? [],
                        };
                    default:
                        return { kind: 'text' };
                }
            });
    }

    private _seedInitial(): void {
        this.documentType.set(null);
        this.quoteStage.set(null);

        const greeting = this._greeting();
        this.messages.set([
            createIaChatMessage({
                sender: 'ia',
                type: 'text',
                text: this._t(greeting.key, greeting.params),
                translationKey: greeting.key,
                translationParams: greeting.params,
            }),
            createIaChatMessage({ sender: 'system', type: 'typeSelection' }),
        ]);
    }

    /**
     * Saudação personalizada e VARIADA: uma de 4 frases, sorteada a cada chat
     * novo, com o primeiro nome do usuário quando existe. O param `name` já
     * carrega a vírgula (", Gui") para a frase sem nome não sobrar pontuação.
     */
    private _greeting(): { key: string; params: Record<string, string> } {
        const variant = 1 + Math.floor(Math.random() * 4);

        let firstName = '';
        try {
            firstName = (this._userController.getCurrentUser()?.name ?? '').trim().split(' ')[0] ?? '';
        } catch {
            firstName = '';
        }

        return {
            key: `iaChat.greeting${variant}`,
            params: { name: firstName ? `, ${firstName}` : '' },
        };
    }

    private _maybeAck(): void {
        if (this._ackedOnce) return;
        this._ackedOnce = true;
        this.isTyping.set(true);
        clearTimeout(this._ackTimer);
        this._ackTimer = setTimeout(() => {
            this.isTyping.set(false);
            this._append(
                createIaChatMessage({
                    sender: 'ia',
                    type: 'text',
                    text: this._t('iaChat.ack'),
                    translationKey: 'iaChat.ack',
                })
            );
        }, 900);
    }

    private _append(message: IaChatMessage): void {
        this.messages.update(messages => [...messages, message]);
        this._persist();
    }

    private _updateMessage(id: string, patch: Partial<IaChatMessage>): void {
        this.messages.update(messages =>
            messages.map(message => (message.id === id ? { ...message, ...patch } : message))
        );
        this._persist();
    }

    private _persist(): void {
        // Conversa encerrada: gravar de novo ressuscitaria um rascunho cujas mídias já
        // pertencem ao documento gerado (ver `_finished`).
        if (this._finished) return;

        const draft: IaChatDraft = {
            documentType: this.documentType(),
            messages: this.messages().map(message => ({
                ...message,
                mediaPaths: message.mediaUrls.length ? [] : message.mediaPaths,
            })),
            quoteStage: this.quoteStage(),
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
    }

    private _t(key: string, params?: Record<string, string>): string {
        return this._translate.instant(key, params);
    }
}
